import * as controllers from 'controllers'
import { PersonDto, SignInDto } from 'dtos'
import { createContext, useContext, useEffect, useState } from 'react'

interface PermissionState {
  ADMINISTRATION_ADD_EDIT: boolean
  CANCEL_IMPORT_CAPABILITY: boolean
  CLUSTERS_ADD_EDIT: boolean
  COMPANIES_AND_PEOPLE_ADD_EDIT: boolean
  COMPANIES_AND_PEOPLE_READ_ONLY: boolean
  CONFORMITY_ADD_EDIT: boolean
  DAMAGE_ADD_EDIT: boolean
  EMPLOYEE: boolean
  INBOUND_TRIPS_ADD_EDIT: boolean
  INVOICES_ADD_EDIT: boolean
  LABEL_PRINTING_ADD_EDIT: boolean
  MISSING_OHIO_TITLE_ALERT: boolean
  NOT_SENT_TO_DOT_AFTER_5_DAYS: boolean
  OCR_ADD_EDIT: boolean
  OUTBOUND_TRIPS_ADD_EDIT: boolean
  RECALL_ADD_EDIT: boolean
  RECEIVES_BOND_BLOCK_ALERT: boolean
  RECEIVES_MISSING_LABEL_EMAILS: boolean
  RECEIVES_SENT_TO_BROKER_EMAILS: boolean
  SPECIAL_BOND_REQUEST: boolean
  TIME_CLOCK: boolean
  TIME_PUNCHES_ADD_EDIT: boolean
  TITLES_ADD_EDIT: boolean
  USERS_ADD_EDIT: boolean
  VEHICLE_CHECK_IN_ADD: boolean
  VEHICLE_CHECK_IN_EDIT: boolean
}

export const AuthContext = createContext<{
  permissions: PermissionState
  signIn: (values: SignInDto) => void
  signOut: () => void
  status: 'loading' | 'authorized' | 'unauthorized'
  user: PersonDto | null
  reports: NonNullable<PersonDto['reports']>
} | null>(null)

export default function AuthProvider({ children }: React.PropsWithChildren) {
  // To add a permission to the front end, update the PermissionState interface above, and add the
  // permission here initialized to false.
  // Please note: The added permission MUST be the permission's special identifier.
  const [permissions, setPermissions] = useState<PermissionState>({
    ADMINISTRATION_ADD_EDIT: false,
    RECEIVES_BOND_BLOCK_ALERT: false,
    CANCEL_IMPORT_CAPABILITY: false,
    CLUSTERS_ADD_EDIT: false,
    COMPANIES_AND_PEOPLE_ADD_EDIT: false,
    COMPANIES_AND_PEOPLE_READ_ONLY: false,
    CONFORMITY_ADD_EDIT: false,
    DAMAGE_ADD_EDIT: false,
    EMPLOYEE: false,
    INBOUND_TRIPS_ADD_EDIT: false,
    INVOICES_ADD_EDIT: false,
    LABEL_PRINTING_ADD_EDIT: false,
    MISSING_OHIO_TITLE_ALERT: false,
    NOT_SENT_TO_DOT_AFTER_5_DAYS: false,
    OCR_ADD_EDIT: false,
    OUTBOUND_TRIPS_ADD_EDIT: false,
    RECALL_ADD_EDIT: false,
    RECEIVES_MISSING_LABEL_EMAILS: false,
    RECEIVES_SENT_TO_BROKER_EMAILS: false,
    SPECIAL_BOND_REQUEST: false,
    TIME_CLOCK: false,
    TIME_PUNCHES_ADD_EDIT: false,
    TITLES_ADD_EDIT: false,
    USERS_ADD_EDIT: false,
    VEHICLE_CHECK_IN_ADD: false,
    VEHICLE_CHECK_IN_EDIT: false,
  })

  const [status, _setStatus] = useState<'loading' | 'authorized' | 'unauthorized'>('unauthorized')
  const [user, _setUser] = useState<PersonDto | null>(null)

  useEffect(() => {
    checkIfUserSignedIn()
  }, [])

  useEffect(() => {
    if (user && user.permissions) {
      let _permissions = permissions

      user.permissions
        .filter(permission => permission.specialIdentifier)
        .forEach(permission => {
          // @ts-ignore
          _permissions[permission.specialIdentifier!] = true
        })

      setPermissions(_permissions)
    }
  }, [user])

  const signIn = ({ email, password }: SignInDto) => {
    _setStatus('loading')
    controllers
      .signIn(email, password)
      .then(({ value }) => {
        setUserAndStatus(value)
      })
      .catch(() => {
        _setStatus('unauthorized')
      })
  }

  const signOut = () => {
    controllers.signOut().then(() => {
      setUserAndStatus(null, 'loading')
      window.location.assign('/account/sign-in')
    })
  }

  // Persist through refreshes
  const checkIfUserSignedIn = () => {
    _setStatus('loading')
    controllers
      .checkIfUserSignedIn()
      .then(({ value }) => {
        setUserAndStatus(value)
      })
      .catch(() => {
        _setStatus('unauthorized')
      })
  }

  const setUserAndStatus = (value: PersonDto | null, status?: 'unauthorized' | 'authorized' | 'loading') => {
    _setUser(value)

    if (status) {
      _setStatus(status)
    } else if (value) {
      _setStatus('authorized')
    } else {
      _setStatus('unauthorized')
    }
  }

  return (
    <AuthContext.Provider
      value={{
        permissions,
        reports: user?.reports ?? [],
        signIn,
        signOut,
        status,
        user
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)!
